import { StatisticType } from "../types/dashboard.type";
import { OrganizationType } from "../types/organization.type";

const ALL_ORG_TYPES: OrganizationType[] = [
  OrganizationType.WASTE_OUTLET,
  OrganizationType.TRUCK,
  OrganizationType.RECYCLER,
  OrganizationType.CHARITY,
];

/** Waste Outlet + Charity (site/container operations, no financial exclusives). */
const SITE_OPS_TYPES: OrganizationType[] = [
  OrganizationType.WASTE_OUTLET,
  OrganizationType.CHARITY,
];

export interface DashboardStatDescriptor {
  stat: StatisticType;
  /** Org types this metric is relevant for. */
  allowedTypes: OrganizationType[];
  /** Pre-selected by default for its allowed types. */
  isDefault?: boolean;
}

/** Dashboard metric catalog — org-type gating (mirrors ZERO_NAV_CATALOG). */
export const DASHBOARD_STAT_CATALOG: DashboardStatDescriptor[] = [
  // ---- Waste Outlet / Charity (site & container ops) ----
  { stat: StatisticType.FULL_PERCENTAGE, allowedTypes: SITE_OPS_TYPES, isDefault: true },
  { stat: StatisticType.STORAGE_CAPACITY_UTILIZATION, allowedTypes: SITE_OPS_TYPES },
  { stat: StatisticType.EMERGENCY_COLLECTION_REQUESTS, allowedTypes: SITE_OPS_TYPES },
  { stat: StatisticType.INCOMING_WASTE_BREAKDOWN, allowedTypes: SITE_OPS_TYPES, isDefault: true },
  { stat: StatisticType.AVERAGE_CONTAINER_FILL_RATE, allowedTypes: SITE_OPS_TYPES },
  { stat: StatisticType.WASTE_COMPACTION_EFFICIENCY, allowedTypes: SITE_OPS_TYPES },

  // ---- Truck / Waste Mover (شركات نقل النفايات) ----
  // Note: ACTIVE_TRUCKS is intentionally not a selectable stat — it is rendered as the Truck Map.
  { stat: StatisticType.TRIPS_TODAY_COUNT, allowedTypes: [OrganizationType.TRUCK], isDefault: true },
  { stat: StatisticType.TRUCK_DRIVER_COUNT, allowedTypes: [OrganizationType.TRUCK] },
  { stat: StatisticType.AVERAGE_ROUTE_EFFICIENCY, allowedTypes: [OrganizationType.TRUCK] },
  { stat: StatisticType.FUEL_CONSUMPTION_PER_TRIP, allowedTypes: [OrganizationType.TRUCK] },
  { stat: StatisticType.DRIVER_PERFORMANCE_SCORE, allowedTypes: [OrganizationType.TRUCK] },
  { stat: StatisticType.MAINTENANCE_SCHEDULE_COMPLIANCE, allowedTypes: [OrganizationType.TRUCK] },
  { stat: StatisticType.TIME_FROM_COLLECTION_TO_DELIVERY, allowedTypes: [OrganizationType.TRUCK], isDefault: true },

  // ---- Recycler (شركات إعادة التدوير) ----
  { stat: StatisticType.RECYCLER_APPETITE, allowedTypes: [OrganizationType.RECYCLER] },
  { stat: StatisticType.SUPPLY_PER_RECYCLER, allowedTypes: [OrganizationType.RECYCLER], isDefault: true },
  { stat: StatisticType.SUSTAINABLE_SUPPLY, allowedTypes: [OrganizationType.RECYCLER] },
  { stat: StatisticType.PROCESSING_CAPACITY_UTILIZATION, allowedTypes: [OrganizationType.RECYCLER], isDefault: true },
  { stat: StatisticType.QUALITY_COMPLIANCE_RATE, allowedTypes: [OrganizationType.RECYCLER] },
  { stat: StatisticType.REJECTED_WASTE_PERCENTAGE, allowedTypes: [OrganizationType.RECYCLER] },

  // ---- Material / sustainability — producers, charities & recyclers (not transporters) ----
  {
    stat: StatisticType.WASTE_TYPE_BREAKDOWN,
    allowedTypes: [...SITE_OPS_TYPES, OrganizationType.RECYCLER],
    isDefault: true,
  },
  {
    stat: StatisticType.WASTE_DIVERSION_RATE,
    allowedTypes: [...SITE_OPS_TYPES, OrganizationType.RECYCLER],
    isDefault: true,
  },
  // Financial — Waste Outlet & Recycler only (not Charity)
  {
    stat: StatisticType.AVERAGE_INCENTIVE_PER_KG,
    allowedTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.RECYCLER],
  },
  { stat: StatisticType.AVERAGE_PROCESSING_TIME, allowedTypes: [OrganizationType.RECYCLER] },

  // ---- Shared across types ----
  // Revenue is only for commercial waste producers (not Charity, transporters, or recyclers).
  { stat: StatisticType.TOTAL_REVENUE, allowedTypes: [OrganizationType.WASTE_OUTLET], isDefault: true },
  { stat: StatisticType.TOTAL_ORDERS, allowedTypes: ALL_ORG_TYPES, isDefault: true },
  { stat: StatisticType.COMPLETION_RATE, allowedTypes: ALL_ORG_TYPES, isDefault: true },
  { stat: StatisticType.CANCELLATION_RATE, allowedTypes: ALL_ORG_TYPES },
  { stat: StatisticType.CUSTOMER_SATISFACTION_SCORE, allowedTypes: [OrganizationType.TRUCK, OrganizationType.RECYCLER] },
  {
    stat: StatisticType.COLLECTION_FREQUENCY,
    allowedTypes: [...SITE_OPS_TYPES, OrganizationType.TRUCK],
  },
  {
    stat: StatisticType.TIME_FROM_FULL_TO_COLLECTION,
    allowedTypes: [...SITE_OPS_TYPES, OrganizationType.TRUCK],
  },
  {
    stat: StatisticType.WASTE_QUALITY_RATING,
    allowedTypes: [...SITE_OPS_TYPES, OrganizationType.RECYCLER],
  },
];

/** Friendlier card titles for Charity dashboards (same underlying metrics). */
const CHARITY_STAT_TITLES: Partial<Record<StatisticType, string>> = {
  [StatisticType.INCOMING_WASTE_BREAKDOWN]: "INCOMING MATERIALS BREAKDOWN",
  [StatisticType.WASTE_TYPE_BREAKDOWN]: "MATERIAL TYPE BREAKDOWN",
  [StatisticType.WASTE_DIVERSION_RATE]: "MATERIAL DIVERSION RATE",
  [StatisticType.FULL_PERCENTAGE]: "CONTAINER FILL LEVEL",
  [StatisticType.EMERGENCY_COLLECTION_REQUESTS]: "URGENT COLLECTION REQUESTS",
  [StatisticType.STORAGE_CAPACITY_UTILIZATION]: "STORAGE CAPACITY USED",
};

export function getStatsForOrgType(orgType: OrganizationType): StatisticType[] {
  return DASHBOARD_STAT_CATALOG
    .filter((d) => d.allowedTypes.includes(orgType))
    .map((d) => d.stat);
}

export function getDefaultStatsForOrgType(orgType: OrganizationType): StatisticType[] {
  return DASHBOARD_STAT_CATALOG
    .filter((d) => d.isDefault && d.allowedTypes.includes(orgType))
    .map((d) => d.stat);
}

export function isStatAllowedForOrgType(
  stat: StatisticType,
  orgType: OrganizationType
): boolean {
  return DASHBOARD_STAT_CATALOG.some(
    (d) => d.stat === stat && d.allowedTypes.includes(orgType)
  );
}

export function getStatDisplayTitle(
  statKey: string,
  orgType?: OrganizationType | null
): string {
  if (orgType === OrganizationType.CHARITY) {
    const charityTitle = CHARITY_STAT_TITLES[statKey as StatisticType];
    if (charityTitle) return charityTitle;
  }
  return statKey.replace(/_/g, " ").toUpperCase();
}

/** Live map / KPI sections that are not StatisticType cards. */
export const DASHBOARD_EXTRA_KPI = "kpi_progress";
export const DASHBOARD_EXTRA_MAP = "map";

export type DashboardExtraWidgetId =
  | typeof DASHBOARD_EXTRA_KPI
  | typeof DASHBOARD_EXTRA_MAP;

export type DashboardViewTab = "monthly" | "overview";

export function getDashboardExtrasForOrgTab(
  orgType: OrganizationType,
  tab: DashboardViewTab,
): DashboardExtraWidgetId[] {
  const extras: DashboardExtraWidgetId[] = [DASHBOARD_EXTRA_KPI];
  const showContainerMap =
    tab === "monthly" &&
    (orgType === OrganizationType.WASTE_OUTLET ||
      orgType === OrganizationType.CHARITY);
  const showTruckMap = orgType === OrganizationType.TRUCK;
  if (showContainerMap || showTruckMap) extras.push(DASHBOARD_EXTRA_MAP);
  return extras;
}

/** Payload fields that are not pickable cards (map data, previous-month revenue). */
export const NON_PICKABLE_STATISTIC_TYPES = new Set<StatisticType>([
  StatisticType.ACTIVE_TRUCKS,
  StatisticType.TOTAL_REVENUE_LAST_MONTH,
]);
